import { type Context, type SystemMessage, type ToolMessage, type UserMessage } from '@ag-ui/core';
import {
  createContext,
  memo,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
} from 'react';
import { v4 as uuid } from 'uuid';

import {
  type AgentChatPromptSource,
  type CreditAnalyticsParams,
  useTrackAgentChatPromptSent,
  useTrackSkillInvoked,
} from '@air/analytics';

import { AgentBusyError, AgentManager } from '../AgentManager';
import { type AiModelConfig } from '../api/aimodels/useAiModels';
import { useBrandKitMentions } from '../api/contextSources/useBrandKitMentions';
import { useSizeMentions } from '../api/contextSources/useSizeMentions';
import { type CanvasThreadListItem } from '../api/threads';
import { type EventSubscriptionManager } from '../EventSubscriptionManager';
import { useThreadManagement } from '../hooks/useThreadManagement';
import { type CanvasTokenNode } from '../store/slices/createCanvasTokenNodeSlice';
import { useBrandKitMentionStore } from '../store/useBrandKitMentionStore';
import { useCanvasTokenNodeStore } from '../store/useCanvasTokenNodeStore';
import { useChatComposerNodeStore } from '../store/useChatComposerNodeStore';
import { useChatComposerStore } from '../store/useChatComposerStore';
import { useChatHistoryNodeStore } from '../store/useChatHistoryNodeStore';
import { getMediaMapNodes, useChatStore } from '../store/useChatStore';
import { getModelSelection, migrateModelSelection } from '../store/useModelSelectionStore';
import { useRunErrorStore } from '../store/useRunErrorStore';
import { findGroupMembers } from '../store/utils/groupUtils';
import { mergeCanvasTokenNodes } from '../store/utils/mergeCanvasTokenNodes';
import { MEDIA_PATTERN } from '../tokens/tokenSerialization';
import {
  type GetAuthToken,
  type ProgrammaticMessageOptions,
  type RequestedVariantCount,
  type ToolResponsePayload,
} from '../types/subscriptions';
import { AgentRunLifecycleBridge } from '../ui/components/AgentRunLifecycleBridge';
import { ChatHistoryNodeSync } from '../ui/components/ChatHistoryNodeSync';
import { type Skill } from '../ui/components/skills';
import { selectSkillInstructionsForMessage } from '../utils/selectSkillInstructionsForMessage';
import { extractSlashCommandsFromMessage } from '../utils/slashCommandInMessage';
import { appendVariantCountInstruction, messageTargetsVariantSkill } from '../utils/variantSkillInMessage';

export type AgentProviderProps = {
  agentUrl: string;
  children: ReactNode;
  firstName?: string;
  getAuthToken: GetAuthToken;
  canConsumeMoreCredits: boolean;
  resourceId: string;
  /** Called when resourceId is empty (draft mode). Must create the resource and return the real ID. */
  resolveResourceId?: () => Promise<string>;
  workspaceId: string;
  creditAnalyticsParams: CreditAnalyticsParams | null;
  onShowPlansModal?: () => void;
  /** Opens the host's edit-skill modal for a skill in the `/` picker. */
  onEditSkill?: (skill: Skill) => void;
  onCreateSkill?: () => void;
  /** Whether the current user can edit a given skill (drives the pencil affordance in the `/` picker). */
  canEditSkill?: (skill: Skill) => boolean;
  onGoToContext?: () => void;
  /**
   * Per-capability model overrides (capability name → model id) the host app merges into the
   * agent's `modelConfig.models`.
   */
  capabilityModelOverrides?: AiModelConfig['models'];
  /** Gates saved-size/set mention queries and the chat follow-up's set-save prompt. Default off. */
  savedSizesEnabled?: boolean;
  /** Whether the current member can create/update saved sizes and sets (read access is implicit). */
  canEditSavedSizes?: boolean;
  canvasAnimateVariantsEnabled?: boolean;
};

export type CancelRunOptions = {
  /** False when the run's source was deleted — restoring would dump a prompt about a missing image. */
  restoreComposer?: boolean;
};

/** A toolbar `actionName` is only allowed together with the `sourceNodeId` it acts on. */
type SendMessageOptions = {
  source?: AgentChatPromptSource;
  sourceNode?: CanvasTokenNode;
} & (
  | { actionName: NonNullable<ProgrammaticMessageOptions['actionName']>; sourceNodeId: string }
  | { actionName?: never; sourceNodeId?: string }
);

export type AgentContextValue = {
  currentThreadId: string | null;
  eventSubscriptions: EventSubscriptionManager | null;
  firstName?: string;
  isLoadingThreads: boolean;
  canConsumeMoreCredits: boolean;
  isRunning: boolean;
  manager: AgentManager | null;
  resourceId: string;
  cancelRun: (threadId: string, options?: CancelRunOptions) => void;
  retryRun: (threadId: string) => void;
  appendUserMessage: (message: string) => Promise<void>;
  sendMessage: (message: string, options?: SendMessageOptions) => Promise<void>;
  sendProgrammaticMessage: (message: string, options?: ProgrammaticMessageOptions) => Promise<void>;
  sendToolResponse: (toolCallId: string, payload: ToolResponsePayload) => Promise<void>;
  setCurrentThreadId: (threadId: string) => void;
  threadError: Error | null;
  threads: CanvasThreadListItem[];
  workspaceId: string;
  creditAnalyticsParams: CreditAnalyticsParams | null;
  onShowPlansModal?: () => void;
  onEditSkill?: (skill: Skill) => void;
  onCreateSkill?: () => void;
  canEditSkill?: (skill: Skill) => boolean;
  onGoToContext?: () => void;
  savedSizesEnabled: boolean;
  canEditSavedSizes: boolean;
  canvasAnimateVariantsEnabled: boolean;
};

const AgentContext = createContext<AgentContextValue | null>(null);

const BUSY_ERROR_AUTO_DISMISS_MS = 120_000;

/**
 * Builds a media context entry from CanvasTokenNode stores for all [media:id]
 * references found in the message. When a sourceNode is provided (e.g. from
 * a transform toolbar), it is included directly so the server receives the
 * full node data for the transform target.
 *
 * Returns null if no references are found.
 */
const buildMediaContext = (message: string, options?: ProgrammaticMessageOptions): Context | null => {
  const mediaMapNodes = getMediaMapNodes(useChatStore.getState());
  // Sync re-keys localId→remoteId and moves the localId entry to inactiveNodes, so a message
  // mentioning the localId only resolves at send time if inactiveNodes is in the merge.
  const { inactiveNodes, nodes: historyNodes } = useChatHistoryNodeStore.getState();
  const merged = {
    ...inactiveNodes,
    ...mediaMapNodes,
    ...mergeCanvasTokenNodes(
      useCanvasTokenNodeStore.getState().nodes,
      historyNodes,
      useChatComposerNodeStore.getState().nodes,
    ),
  };

  const referencedNodes: Record<string, (typeof merged)[string]> = {};

  for (const match of message.matchAll(MEDIA_PATTERN)) {
    const id = match[1];
    const node = merged[id];

    if (node) {
      referencedNodes[id] = node;
    } else {
      // id may be a groupId — include all member nodes so the agent receives the full group
      const members = findGroupMembers(merged, id);
      for (const [memberKey, memberNode] of members) {
        referencedNodes[memberKey] = memberNode;
      }
    }
  }

  // Include the source node (transform target) if provided directly from the callsite.
  // Key by sourceNodeId (the local id embedded in the message as [media:id]) so the AI
  // can match the context to the reference in the prompt.
  if (options?.sourceNode) {
    const key = options.sourceNodeId ?? options.sourceNode?.group?.id;

    if (key) {
      referencedNodes[key] = options.sourceNode;
    }
  }

  if (Object.keys(referencedNodes).length === 0) {
    return null;
  }

  return { description: 'media', value: JSON.stringify(referencedNodes) };
};

/**
 * Builds the `modelConfig` context entry (0 or 1 entries). When `capabilityModelOverrides`
 * are provided, merges them into the entry's `models` map. The backend reads only the
 * first `modelConfig` entry, so overrides must live on that single entry — including one
 * created from scratch when the canvas is on Auto (no model selected). Without overrides,
 * behavior is unchanged.
 */
const buildModelConfigContext = (
  model: AiModelConfig | null,
  capabilityModelOverrides: AiModelConfig['models'] | undefined,
): Context[] => {
  if (!capabilityModelOverrides || Object.keys(capabilityModelOverrides).length === 0) {
    return model ? [{ description: 'modelConfig', value: JSON.stringify(model) }] : [];
  }

  const mergedConfig = model
    ? { ...model, models: { ...model.models, ...capabilityModelOverrides } }
    : { models: { ...capabilityModelOverrides } };

  return [{ description: 'modelConfig', value: JSON.stringify(mergedConfig) }];
};

export const AgentProvider = memo(
  ({
    agentUrl,
    canConsumeMoreCredits,
    children,
    firstName,
    getAuthToken,
    resourceId,
    resolveResourceId,
    workspaceId,
    creditAnalyticsParams,
    onShowPlansModal,
    onEditSkill,
    onCreateSkill,
    canEditSkill,
    onGoToContext,
    capabilityModelOverrides,
    savedSizesEnabled = false,
    canEditSavedSizes = false,
    canvasAnimateVariantsEnabled = false,
  }: AgentProviderProps) => {
    const { trackAgentChatPromptSent } = useTrackAgentChatPromptSent();
    const { trackSkillInvoked } = useTrackSkillInvoked();
    const { brandKitMentions } = useBrandKitMentions(workspaceId);
    const { sizeMentions } = useSizeMentions({ enabled: savedSizesEnabled, workspaceId });

    // Resets immediately on a workspace switch so the prior workspace's mentions (including its
    // saved sizes/sets) can't linger onto the new one while its query is still in flight.
    const prevWorkspaceIdRef = useRef(workspaceId);

    useEffect(() => {
      if (prevWorkspaceIdRef.current !== workspaceId) {
        prevWorkspaceIdRef.current = workspaceId;
        useBrandKitMentionStore.getState().setMentions([]);
      }
    }, [workspaceId]);

    useEffect(() => {
      useBrandKitMentionStore.getState().setMentions([...sizeMentions, ...brandKitMentions]);
    }, [brandKitMentions, sizeMentions]);

    useEffect(() => () => useBrandKitMentionStore.getState().setMentions([]), []);

    // Migrate during render, not useEffect — child effects (e.g. ModelSelector's
    // validateModelSelection) run before parent effects, so a useEffect here
    // would race: the validation effect would see no entry under the new id
    // and re-initialize with isAuto: true.
    const prevResourceIdRef = useRef(resourceId);

    if (prevResourceIdRef.current !== resourceId) {
      const prev = prevResourceIdRef.current;
      prevResourceIdRef.current = resourceId;

      if (prev === '' && resourceId !== '') {
        migrateModelSelection(prev, resourceId);
      }
    }

    const {
      createThread,
      currentThreadId,
      isCurrentThreadHydrated,
      isLoadingThreads,
      setCurrentThreadId,
      threadError,
      threads,
    } = useThreadManagement({
      resolveResourceId,
      resourceId,
      workspaceId,
    });
    const managerRef = useRef<AgentManager | null>(null);
    const busyErrorTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
    const cancelledDuringSendRef = useRef(new Set<string>());

    // Ref for imperative access to currentThreadId in callbacks
    const currentThreadIdRef = useRef(currentThreadId);
    currentThreadIdRef.current = currentThreadId;

    // Keep a ref to the latest getAuthToken callback so the stable wrapper
    // always calls the most recent version without triggering manager recreation.
    const getAuthTokenRef = useRef(getAuthToken);
    getAuthTokenRef.current = getAuthToken;

    // Create AgentManager — only recreated when agentUrl or workspaceId changes.
    // resourceId updates are applied imperatively via setResourceId to avoid
    // destroying in-flight agent runs during draft→real canvas commit.
    // biome-ignore lint/correctness/useExhaustiveDependencies(resourceId): updated imperatively via manager.setResourceId
    const manager = useMemo(() => {
      managerRef.current?.dispose();

      const newManager = new AgentManager({
        agentUrl,
        getAuthToken: (options) => getAuthTokenRef.current(options),
        getThreadData: (threadId) => useChatStore.getState().threads[threadId] ?? null,
        onMessagesChanged: (threadId, messages) => {
          useChatStore.getState().setMessagesForThread(threadId, messages);
        },
        onStateChanged: (threadId, newState) => {
          useChatStore.getState().setStateForThread(threadId, newState);
        },
        resourceId,
        workspaceId,
      });

      managerRef.current = newManager;
      return newManager;
    }, [agentUrl, workspaceId]);

    // Keep the manager's resourceId in sync without recreating it
    useEffect(() => {
      if (manager && resourceId) {
        manager.setResourceId(resourceId);
      }
    }, [manager, resourceId]);

    useEffect(() => {
      return () => {
        managerRef.current?.dispose();
        managerRef.current = null;
      };
    }, []);

    useEffect(() => {
      return () => {
        busyErrorTimersRef.current.forEach(clearTimeout);
      };
    }, []);

    // Wait for store hydration (S3 messages loaded) before creating the agent,
    // so it initializes with the correct message history rather than an empty array.
    useEffect(() => {
      if (manager && currentThreadId && isCurrentThreadHydrated) {
        manager.getAgent(currentThreadId);
      }
    }, [manager, currentThreadId, isCurrentThreadHydrated]);

    // Clear any stale run error when switching threads so it doesn't reappear on return.
    useEffect(() => {
      if (currentThreadId) {
        useRunErrorStore.getState().clearRunError(currentThreadId);
      }
    }, [currentThreadId]);

    // Guard against concurrent thread creation (e.g. rapid programmatic sends)
    const creatingThreadRef = useRef<Promise<{ threadId: string; resolvedResourceId: string }> | null>(null);
    const ensureThreadId = useCallback(async (): Promise<{ threadId: string; resolvedResourceId: string }> => {
      const existing = currentThreadIdRef.current;

      if (existing) {
        return { resolvedResourceId: resourceId, threadId: existing };
      }

      // createThread() is the guarded version from useThreadManagement — it
      // reuses an existing blank thread rather than creating duplicates.
      // biome-ignore lint/suspicious/noUnnecessaryConditions: ref writes are invisible to the rule; this varies at runtime.
      if (!creatingThreadRef.current) {
        creatingThreadRef.current = createThread().then(({ thread, resolvedResourceId }) => {
          // Thread id is already set by `guardedCreateThread` → `persistAndSetThread` (without clearing skills).
          manager?.getAgent(thread.id);
          return { resolvedResourceId, threadId: thread.id };
        });
      }

      try {
        return await creatingThreadRef.current;
      } finally {
        creatingThreadRef.current = null;
      }
    }, [createThread, manager, resourceId]);

    const isRunning = useSyncExternalStore(
      useCallback(
        (onStoreChange) => {
          if (!manager) {
            return () => {};
          }

          return manager.subscribeToRunningState(onStoreChange);
        },
        [manager],
      ),
      useCallback(() => {
        if (!manager || !currentThreadId) {
          return false;
        }

        return manager.isRunning(currentThreadId);
      }, [manager, currentThreadId]),
    );

    const handleAgentBusyError = useCallback((threadId: string, error: AgentBusyError, composerText?: string) => {
      if (composerText) {
        useChatComposerStore.getState().messageText?.(composerText);
      }
      useRunErrorStore.getState().setRunError(threadId, {
        code: 'AGENT_BUSY',
        message: error.message,
      });
      // Cancel any existing auto-dismiss timer for this thread so a second busy error
      // doesn't get cleared prematurely by the first timer.
      clearTimeout(busyErrorTimersRef.current.get(threadId));
      busyErrorTimersRef.current.set(
        threadId,
        setTimeout(() => {
          useRunErrorStore.getState().clearRunError(threadId);
          busyErrorTimersRef.current.delete(threadId);
        }, BUSY_ERROR_AUTO_DISMISS_MS),
      );
    }, []);

    const sendMessage = useCallback(
      async (message: string, options?: SendMessageOptions) => {
        if (!manager) {
          return;
        }

        // A toolbar action sends a visible message without using or clearing the composer draft.
        const isToolbarAction = Boolean(options?.actionName);
        const composerStore = useChatComposerStore.getState();
        const pendingSkills = isToolbarAction ? [] : composerStore.pendingSkillInstructions;

        const { threadId, resolvedResourceId } = await ensureThreadId();
        const commandsInMessage = extractSlashCommandsFromMessage(message);
        const matchedSkills = pendingSkills.filter((skill) => commandsInMessage.has(skill.slashCommand));

        trackAgentChatPromptSent({
          canvas_id: resolvedResourceId,
          message_length: message.length,
          slash_commands: matchedSkills.map((skill) => skill.slashCommand),
          source: options?.source ?? 'sidebar',
          thread_id: threadId,
        });

        const agUiMessage: UserMessage = {
          content: message,
          id: `msg_${uuid()}`,
          role: 'user',
        };

        const { selectedModel, isAuto } = getModelSelection(resolvedResourceId);
        const mediaContext = buildMediaContext(message, options);
        for (const skill of matchedSkills) {
          trackSkillInvoked({
            canvas_id: resolvedResourceId,
            is_invoked_from_onboarding: skill.isInvokedFromOnboarding ?? false,
            skill_id: skill.id,
            slash_command: skill.slashCommand,
          });
        }
        const composerVariantCount: RequestedVariantCount | undefined =
          canvasAnimateVariantsEnabled &&
          messageTargetsVariantSkill(message) &&
          !matchedSkills.some((skill) => skill.skipComposerVariantCount)
            ? { count: composerStore.variantCount, source: 'composer_pill' }
            : manager.getVariantCountAwaitingReply(threadId);
        const variantCount = isToolbarAction ? undefined : composerVariantCount;
        const skillInstructions = selectSkillInstructionsForMessage(pendingSkills, message);
        const skillInstructionsValue = variantCount
          ? appendVariantCountInstruction(skillInstructions, variantCount.count)
          : skillInstructions;

        const context: Context[] = [
          ...buildModelConfigContext(isAuto ? null : selectedModel, capabilityModelOverrides),
          ...(mediaContext ? [mediaContext] : []),
          ...(skillInstructionsValue ? [{ description: 'skillInstructions', value: skillInstructionsValue }] : []),
        ];

        cancelledDuringSendRef.current.delete(threadId);
        try {
          await manager.sendMessage({
            context: context.length > 0 ? context : undefined,
            messages: [agUiMessage],
            // Carry the in-place edit target (a grouped layer) so the tool result
            // replaces that layer instead of appending, and the toolbar action name for
            // result routing. This stays a normal user message — no `programmatic` flag —
            // so it still renders in history.
            programmaticOptions: options?.sourceNodeId
              ? { actionName: options.actionName, sourceNode: options.sourceNode, sourceNodeId: options.sourceNodeId }
              : undefined,
            threadId,
            variantCount,
          });
          if (!isToolbarAction && !cancelledDuringSendRef.current.has(threadId)) {
            useChatComposerStore.getState().clearPendingSkillInstructions();
          }
          cancelledDuringSendRef.current.delete(threadId);
        } catch (error) {
          if (error instanceof AgentBusyError) {
            // Only a typed message goes back into the composer.
            const composerText = isToolbarAction ? undefined : message;
            handleAgentBusyError(threadId, error, composerText);
            return;
          }
          // Clear on non-busy errors so stale instructions don't attach to a future retry.
          cancelledDuringSendRef.current.delete(threadId);
          if (!isToolbarAction) {
            useChatComposerStore.getState().clearPendingSkillInstructions();
          }
          throw error;
        }
      },
      [
        ensureThreadId,
        manager,
        capabilityModelOverrides,
        canvasAnimateVariantsEnabled,
        handleAgentBusyError,
        trackAgentChatPromptSent,
        trackSkillInvoked,
      ],
    );

    const retryRun = useCallback(
      (threadId: string) => {
        if (!manager) {
          return;
        }

        // Fire-and-forget: the caller is a click handler with nowhere to await, so this
        // never rejects — every failure is routed to a banner instead.
        manager.retryRun(threadId).catch((error: unknown) => {
          // In-run failures already surfaced a (possibly more specific) banner via the
          // manager — keep it. Only pre-run failures with no banner of their own
          // (e.g. a token fetch failure) get the generic one.
          if (!useRunErrorStore.getState().byThread[threadId]) {
            useRunErrorStore.getState().setRunError(threadId, {
              code: 'UNKNOWN',
              message: error instanceof Error ? error.message : 'Retry failed',
            });
          }
        });
      },
      [manager],
    );

    const appendUserMessage = useCallback(
      async (message: string) => {
        if (!manager) {
          return;
        }

        const { threadId } = await ensureThreadId();
        manager.appendUserMessage(threadId, message);
      },
      [ensureThreadId, manager],
    );

    const sendProgrammaticMessage = useCallback(
      async (message: string, options?: ProgrammaticMessageOptions) => {
        if (!manager) {
          return;
        }

        const { threadId, resolvedResourceId } = await ensureThreadId();

        const agUiMessage: SystemMessage = {
          content: message,
          id: `msg_${uuid()}`,
          role: 'system',
        };

        const { selectedModel, isAuto } = getModelSelection(resolvedResourceId);
        // A per-call modelOverride wins over the canvas-wide selection so transforms can lock
        // the model regardless of what the user (or auto-selection) has chosen.
        const effectiveModel = options?.modelOverride ?? (isAuto ? null : selectedModel);
        const mediaContext = buildMediaContext(message, options);
        const context: Context[] = [
          { description: 'programmatic', value: 'true' },
          ...(options?.actionName ? [{ description: 'programmaticAction', value: options.actionName }] : []),
          ...buildModelConfigContext(effectiveModel, capabilityModelOverrides),
          ...(mediaContext ? [mediaContext] : []),
        ];

        await manager.sendMessage({
          context: context.length > 0 ? context : undefined,
          messages: [agUiMessage],
          programmaticOptions: options,
          threadId,
          variantCount: options?.variantCount ? { count: options.variantCount, source: 'toolbar_pill' } : undefined,
        });
      },
      [ensureThreadId, manager, capabilityModelOverrides],
    );

    const sendToolResponse = useCallback(
      async (toolCallId: string, payload: ToolResponsePayload) => {
        if (!manager) {
          return;
        }

        const { threadId } = await ensureThreadId();

        const toolMessage: ToolMessage = {
          content: JSON.stringify(payload),
          id: `msg_${uuid()}`,
          role: 'tool',
          toolCallId,
        };

        await manager.sendMessage({
          messages: [toolMessage],
          threadId,
        });
      },
      [manager, ensureThreadId],
    );

    const cancelRun = useCallback(
      (threadId: string, options?: CancelRunOptions) => {
        if (!manager) {
          return;
        }
        cancelledDuringSendRef.current.add(threadId);
        const lastMessage = manager.cancelRun(threadId);

        // Stop puts the prompt back so you can edit and resend. Deleting the source
        // image must not — the restored [media:] + "turn this image into a video"
        // is a prompt about something that is no longer on the canvas.
        if (options?.restoreComposer === false || !lastMessage) {
          return;
        }

        const composerStore = useChatComposerStore.getState();
        const hasSkillSuffix = composerStore.pendingSkillInstructions.some((s) =>
          lastMessage.endsWith(`/${s.slashCommand}`),
        );
        composerStore.messageText?.(hasSkillSuffix ? `${lastMessage} ` : lastMessage);
      },
      [manager],
    );

    const value = useMemo<AgentContextValue>(
      () => ({
        appendUserMessage,
        canConsumeMoreCredits,
        cancelRun,
        canEditSavedSizes,
        canEditSkill,
        canvasAnimateVariantsEnabled,
        creditAnalyticsParams,
        currentThreadId,
        eventSubscriptions: manager?.eventSubscriptions ?? null,
        firstName,
        isLoadingThreads,
        isRunning,
        manager,
        onCreateSkill,
        onEditSkill,
        onGoToContext,
        onShowPlansModal,
        resourceId,
        retryRun,
        savedSizesEnabled,
        sendMessage,
        sendProgrammaticMessage,
        sendToolResponse,
        setCurrentThreadId,
        threadError,
        threads,
        workspaceId,
      }),
      [
        cancelRun,
        retryRun,
        currentThreadId,
        firstName,
        isLoadingThreads,
        canConsumeMoreCredits,
        isRunning,
        manager,
        resourceId,
        appendUserMessage,
        sendMessage,
        sendProgrammaticMessage,
        setCurrentThreadId,
        threadError,
        threads,
        workspaceId,
        sendToolResponse,
        creditAnalyticsParams,
        onShowPlansModal,
        onEditSkill,
        onCreateSkill,
        onGoToContext,
        canEditSkill,
        canEditSavedSizes,
        savedSizesEnabled,
        canvasAnimateVariantsEnabled,
      ],
    );

    return (
      <AgentContext.Provider value={value}>
        <ChatHistoryNodeSync />
        <AgentRunLifecycleBridge />
        {children}
      </AgentContext.Provider>
    );
  },
);

AgentProvider.displayName = 'AgentProvider';

export const useAgentManager = () => {
  const context = useContext(AgentContext);

  if (!context) {
    throw new Error('useAgentManager must be used within an AgentProvider');
  }

  return context;
};
